import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useGeneralRefetchOverride } from "src/queries/general.queries";
import { QueryNames } from "src/queries/queries.constant";
import { getApiLanguage } from "src/services/commonLanguageHeader";
import {
  createTirePreset,
  deleteTirePreset,
  getTirePresetById,
  getTirePresetsList,
  updateTirePreset,
  updateTirePresetStatus,
  type TirePresetListParams,
  type TirePresetPayload,
  type TirePresetStatusPayload,
} from "../../services/administrator/tirePresetService";

export const useQueryGetTirePresetsList = (
  params: TirePresetListParams = {},
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.tirePresetList, params, language],
      queryFn: () => getTirePresetsList(params, language),
      refetchOnMount: true,
      retry: false,
      ...options,
    }),
    options.enabled !== false
  );
};

export const useQueryGetTirePresetById = (
  id?: string | number,
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: [...QueryNames.tirePresetDetail, id, language],
    queryFn: () => getTirePresetById(id!, language),
    enabled: Boolean(id) && options.enabled !== false,
    refetchOnMount: "always",
    retry: false,
    staleTime: 0,
    ...options,
  });
};

export const useMutationCreateTirePreset = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (payload: TirePresetPayload) => createTirePreset(payload, language),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: QueryNames.tirePresetList,
      }),
  });
};

export const useMutationUpdateTirePreset = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string | number;
      payload: TirePresetPayload & { id?: string | number };
    }) => updateTirePreset(id, payload, language),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: QueryNames.tirePresetList,
        }),
        queryClient.invalidateQueries({
          queryKey: QueryNames.tirePresetDetail,
          refetchType: "none",
        }),
      ]),
  });
};

export const useMutationUpdateTirePresetStatus = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: ({ id, active }: TirePresetStatusPayload) =>
      updateTirePresetStatus(id, { id, active }, language),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: QueryNames.tirePresetList,
      }),
  });
};

export const useMutationDeleteTirePreset = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (id: string | number) => deleteTirePreset(id, language),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: QueryNames.tirePresetList,
      }),
  });
};
